import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Check,
  ChevronLeft,
  X,
  DollarSign,
  Hash,
  Target,
  AlertTriangle,
  ClipboardList,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
const API_BASE_URL =
  process.env.REACT_APP_BASE_URL || "http://localhost:3000/api/v1";

const CampaignDetails = () => {
  const { isDarkMode } = useTheme();
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const location = useLocation();

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      navigate("/login");
      return;
    }

    const fetchCampaignDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/campaigns/${campaignId}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        if (
          response.data.statusCode === 401 ||
          response.data.message === "Unauthorized"
        ) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          window.location.href = "/login";
          return;
        }

        setCampaign(response.data.data);
      } catch (err) {
        if (
          err.response?.status === 401 ||
          err.response?.data?.message === "Unauthorized"
        ) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          window.location.href = "/login";
          return;
        }

        console.error("Failed to fetch campaign details:", err);
        const errorMessage =
          err.response?.data?.message || "Failed to fetch campaign details.";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (campaignId) fetchCampaignDetails();
  }, [campaignId, navigate]);

  const handleBack = () => {
    if (location.state?.from === "homepage") {
      navigate("/");
    } else {
      navigate("/campaigns");
    }
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    setError(null);
    const accessToken = localStorage.getItem("accessToken");
    try {
      await axios.put(
        `${API_BASE_URL}/campaigns/admin/${campaignId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (location.state?.from === "homepage") {
        navigate("/");
      } else {
        navigate("/campaigns");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to approve campaign.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectClick = () => setShowRejectModal(true);

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      setError("Please provide a reason for rejection.");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    const accessToken = localStorage.getItem("accessToken");
    try {
      await axios.put(
        `${API_BASE_URL}/campaigns/admin/${campaignId}/reject`,
        { reason: rejectionReason },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      navigate("/campaigns");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reject campaign.");
      setIsSubmitting(false);
    }
  };

  const handleRejectCancel = () => {
    setShowRejectModal(false);
    setRejectionReason("");
    setError(null);
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      draft: isDarkMode
        ? "bg-gray-500/20 text-gray-400 border-gray-500/30"
        : "bg-gray-100 text-gray-700 border-gray-300",
      pending_approval: isDarkMode
        ? "bg-orange-500/20 text-orange-400 border-orange-500/30"
        : "bg-orange-100 text-orange-700 border-orange-300",
      approved: isDarkMode
        ? "bg-green-500/20 text-green-400 border-green-500/30"
        : "bg-green-100 text-green-700 border-green-300",
      active: isDarkMode
        ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
        : "bg-blue-100 text-blue-700 border-blue-300",
      completed: isDarkMode
        ? "bg-purple-500/20 text-purple-400 border-purple-500/30"
        : "bg-purple-100 text-purple-700 border-purple-300",
      rejected: isDarkMode
        ? "bg-red-500/20 text-red-400 border-red-500/30"
        : "bg-red-100 text-red-700 border-red-300",
      paused: isDarkMode
        ? "bg-orange-500/20 text-orange-400 border-orange-500/30"
        : "bg-orange-100 text-orange-700 border-orange-300",
      cancelled: isDarkMode
        ? "bg-red-500/20 text-red-400 border-red-500/30"
        : "bg-red-100 text-red-700 border-red-300",
    };

    const displayStatus = status
      ? status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      : "Draft";

    return (
      <span
        className={`px-3 py-1.5 text-sm font-semibold rounded-full border ${
          styles[status] || styles.draft
        }`}
      >
        {displayStatus}
      </span>
    );
  };

  const ApprovalStatusBadge = ({ approvalStatus }) => {
    const styles = {
      pending: isDarkMode
        ? "bg-orange-500/20 text-orange-400 border-orange-500/30"
        : "bg-orange-100 text-orange-700 border-orange-300",
      approved: isDarkMode
        ? "bg-green-500/20 text-green-400 border-green-500/30"
        : "bg-green-100 text-green-700 border-green-300",
      rejected: isDarkMode
        ? "bg-red-500/20 text-red-400 border-red-500/30"
        : "bg-red-100 text-red-700 border-red-300",
    };

    return (
      <span
        className={`px-3 py-1.5 text-sm font-semibold rounded-full border ${
          styles[approvalStatus] || styles.pending
        }`}
      >
        {approvalStatus
          ? approvalStatus.charAt(0).toUpperCase() + approvalStatus.slice(1)
          : "Pending"}
      </span>
    );
  };

  if (loading) {
    return (
      <div
        className={`flex flex-col min-h-screen ${
          isDarkMode ? "bg-black" : "bg-gray-100"
        }`}
      >
        <div
          className={`flex-grow flex items-center justify-center ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-400 mx-auto mb-4"></div>
            <p className="text-xl">Loading campaign details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !campaign) {
    return (
      <div
        className={`flex flex-col min-h-screen ${
          isDarkMode ? "bg-black" : "bg-gray-100"
        }`}
      >
        <div className="flex-grow flex items-center justify-center p-5">
          <div className="bg-red-900/50 border border-red-500 rounded-xl p-4 text-red-200 max-w-md">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div
        className={`flex flex-col min-h-screen ${
          isDarkMode ? "bg-black" : "bg-gray-100"
        }`}
      >
        <div
          className={`flex-grow flex items-center justify-center ${
            isDarkMode ? "text-gray-500" : "text-gray-600"
          }`}
        >
          Campaign not found.
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? "bg-black text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="flex flex-col gap-5 p-5">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className={`flex items-center gap-2 w-fit ${
            isDarkMode
              ? "text-gray-500 hover:text-white"
              : "text-gray-600 hover:text-gray-900"
          } transition-colors`}
        >
          <ChevronLeft size={20} />
          <span className="text-sm font-medium">
            {location.state?.from === "homepage"
              ? "Back to Dashboard"
              : "Back to Campaigns"}
          </span>
        </button>

        {/* Error Banner */}
        {error && (
          <div className="bg-red-900/50 border border-red-500 rounded-xl p-4 text-red-200">
            {error}
          </div>
        )}

        {/* Main Campaign Card */}
        <div
          className={`${
            isDarkMode ? "bg-black border-zinc-900" : "bg-white border-gray-200"
          } border rounded-xl p-6 space-y-6`}
        >
          {/* Header Section */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2">
                {campaign.title || "Untitled Campaign"}
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <StatusBadge status={campaign.status} />
                <div className="flex items-center gap-2">
                  <DollarSign className="text-orange-400" size={18} />
                  <span
                    className={`text-lg font-semibold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    ${campaign.totalTokenPool?.toLocaleString() || "0"}
                  </span>
                  <span
                    className={isDarkMode ? "text-gray-500" : "text-gray-600"}
                  >
                    Total Pool
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {campaign.approvalStatus === "pending" &&
              campaign.status !== "completed" &&
              campaign.status !== "COMPLETED" && (
                <div className="flex gap-3">
                  <button
                    onClick={handleApprove}
                    disabled={isSubmitting}
                    className="group relative px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white font-semibold rounded-lg overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-green-500/50 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                    <div className="relative flex items-center gap-2">
                      <Check size={20} />
                      <span>{isSubmitting ? "Processing..." : "Approve"}</span>
                    </div>
                  </button>

                  <button
                    onClick={handleRejectClick}
                    disabled={isSubmitting}
                    className="group relative px-6 py-3 bg-gradient-to-r from-red-600 to-red-500 text-white font-semibold rounded-lg overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-red-500/50 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                    <div className="relative flex items-center gap-2">
                      <X size={20} />
                      <span>{isSubmitting ? "Processing..." : "Reject"}</span>
                    </div>
                  </button>
                </div>
              )}
          </div>

          {/* Divider */}
          {/* Divider */}
          <div
            className={`border-t ${
              isDarkMode ? "border-zinc-900" : "border-gray-200"
            }`}
          ></div>

          {/* Campaign Details - Column Layout */}
          <div className="space-y-6">
            {/* Campaign Objective */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Target className="text-orange-400" size={20} />
                <h2 className="text-xl font-semibold">Campaign Objective</h2>
              </div>
              <p
                className={`leading-relaxed ${
                  isDarkMode ? "text-gray-400" : "text-gray-700"
                }`}
              >
                {campaign.description || "No description available"}
              </p>
            </div>

            {/* Campaign Rules */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ClipboardList className="text-orange-400" size={20} />
                <h2 className="text-xl font-semibold">Campaign Rules</h2>
              </div>

              {campaign.rules && campaign.rules.length > 0 ? (
                <div className="space-y-3">
                  {/* Rules in Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {campaign.rules.map((rule, i) => (
                      <div
                        key={i}
                        className={`p-4 rounded-lg border ${
                          isDarkMode
                            ? "bg-zinc-900/50 border-zinc-800"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Check
                            className="text-green-400 mt-0.5 flex-shrink-0"
                            size={16}
                          />
                          <div className="flex-1 space-y-1.5">
                            {/* Rule Type */}
                            <div className="font-semibold">
                              {rule.ruleType.replace(/_/g, " ")}
                            </div>

                            {/* Rule Details */}
                            <div
                              className={`space-y-0.5 text-sm ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {/* TOP_N specific */}
                              {rule.ruleType === "TOP_N" && (
                                <>
                                  {rule.topUsersCount && (
                                    <div>
                                      Top{" "}
                                      <strong
                                        className={
                                          isDarkMode
                                            ? "text-gray-300"
                                            : "text-gray-700"
                                        }
                                      >
                                        {rule.topUsersCount}
                                      </strong>{" "}
                                      users qualify
                                    </div>
                                  )}
                                  {rule.topUsersPercentage && (
                                    <div>
                                      Each receives{" "}
                                      <strong
                                        className={
                                          isDarkMode
                                            ? "text-gray-300"
                                            : "text-gray-700"
                                        }
                                      >
                                        {rule.topUsersPercentage}%
                                      </strong>{" "}
                                      of their allocation
                                    </div>
                                  )}
                                </>
                              )}

                              {/* MOST_LIKED_POST specific */}
                              {rule.ruleType === "MOST_LIKED_POST" && (
                                <div>Most liked post wins this reward</div>
                              )}

                              {/* ALL_PARTICIPANTS specific */}
                              {rule.ruleType === "ALL_PARTICIPANTS" &&
                                rule.remainingUsersPercentage && (
                                  <div>
                                    Remaining users get{" "}
                                    <strong
                                      className={
                                        isDarkMode
                                          ? "text-gray-300"
                                          : "text-gray-700"
                                      }
                                    >
                                      {rule.remainingUsersPercentage}%
                                    </strong>{" "}
                                    each
                                  </div>
                                )}

                              {/* Common fields */}
                              {rule.totalPoolPercentage && (
                                <div>
                                  Allocated{" "}
                                  <strong
                                    className={
                                      isDarkMode
                                        ? "text-gray-300"
                                        : "text-gray-700"
                                    }
                                  >
                                    {rule.totalPoolPercentage}%
                                  </strong>{" "}
                                  of total pool
                                </div>
                              )}

                              {rule.equalDistribution !== undefined && (
                                <div>
                                  Distribution:{" "}
                                  <strong
                                    className={
                                      isDarkMode
                                        ? "text-gray-300"
                                        : "text-gray-700"
                                    }
                                  >
                                    {rule.equalDistribution
                                      ? "Equal"
                                      : "Proportional"}
                                  </strong>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Total Pool Summary */}
                  <div
                    className={`p-3 rounded-lg text-sm ${
                      isDarkMode
                        ? "bg-orange-400/10 text-orange-400 border border-orange-400/20"
                        : "bg-orange-50 text-orange-700 border border-orange-200"
                    }`}
                  >
                    <strong>Total Reward Pool:</strong>{" "}
                    {campaign.totalTokenPool ||
                      campaign.rewardStructure?.totalPool ||
                      0}{" "}
                    tokens
                  </div>
                </div>
              ) : (
                <p className={isDarkMode ? "text-gray-500" : "text-gray-600"}>
                  No rules specified
                </p>
              )}
            </div>
          </div>
          {/* Additional Info */}
          <div
            className={`border-t ${
              isDarkMode ? "border-zinc-900" : "border-gray-200"
            }`}
          ></div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className={isDarkMode ? "text-gray-500" : "text-gray-600"}>
                Category
              </p>
              <p className="font-semibold">{campaign.category || "N/A"}</p>
            </div>
            <div>
              <p className={isDarkMode ? "text-gray-500" : "text-gray-600"}>
                Participants
              </p>
              <p className="font-semibold">{campaign.participantCount || 0}</p>
            </div>
          </div>

          {/* Tags */}
          {campaign.tags && campaign.tags.length > 0 && (
            <>
              <div
                className={`border-t ${
                  isDarkMode ? "border-zinc-900" : "border-gray-200"
                }`}
              ></div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Hash className="text-orange-400" size={20} />
                  <h2 className="text-xl font-semibold">Tags</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {campaign.tags.map((tag, i) => (
                    <span
                      key={i}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                        isDarkMode
                          ? "bg-zinc-900 text-gray-400"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div
            className={`rounded-2xl shadow-2xl max-w-md w-full ${
              isDarkMode
                ? "bg-black border border-zinc-900"
                : "bg-white border border-gray-200"
            } animate-in zoom-in-95 duration-200`}
          >
            {/* Modal Header */}
            <div
              className={`p-6 border-b ${
                isDarkMode ? "border-zinc-900" : "border-gray-200"
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <AlertTriangle className="text-red-400" size={24} />
                </div>
                <h3 className="text-xl font-bold">Reject Campaign</h3>
              </div>
              <p className={isDarkMode ? "text-gray-500" : "text-gray-600"}>
                Please provide a reason for rejecting this campaign. This will
                help the creator understand your decision.
              </p>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <label
                  className={`block text-sm font-medium ${
                    isDarkMode ? "text-gray-400" : "text-gray-700"
                  }`}
                >
                  Rejection Reason
                </label>
                <span
                  className={`text-xs ${
                    rejectionReason.length > 500
                      ? "text-red-400"
                      : isDarkMode
                      ? "text-gray-500"
                      : "text-gray-500"
                  }`}
                >
                  {rejectionReason.length}/500
                </span>
              </div>
              <textarea
                value={rejectionReason}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setRejectionReason(e.target.value);
                  }
                }}
                placeholder="e.g., Content does not meet quality standards..."
                rows="4"
                maxLength={500}
                className={`w-full rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none border ${
                  isDarkMode
                    ? "bg-black text-white border-zinc-800 placeholder-gray-500"
                    : "bg-gray-50 text-gray-900 border-gray-300 placeholder-gray-400"
                }`}
              />
            </div>

            {/* Modal Footer */}
            <div
              className={`p-6 border-t flex gap-3 ${
                isDarkMode ? "border-zinc-900" : "border-gray-200"
              }`}
            >
              <button
                onClick={handleRejectCancel}
                disabled={isSubmitting}
                className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? "bg-zinc-900 text-gray-400 hover:bg-zinc-800"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={isSubmitting || !rejectionReason.trim()}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-600 to-red-500 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-red-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Submitting..." : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignDetails;
